import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Users, User } from "lucide-react";
import { useSecureChat } from "./SecureChatContext";
import ChatListItem from "./ChatListItem";
import NewChatModal from "./NewChatModal";
import NewGroupModal from "./NewGroupModal";
import {
  onSecureChatCreated,
  onSecureChatUpdated,
} from "../../api/secureSocket";
const api = require("../../api/secureMessageApi");

// Helper: get detailed sort info (timestamp + which field was used)
const getChatSortInfo = (chat) => {
  if (!chat) {
    return {
      timestamp: 0,
      source: null,
      rawValue: null,
      latestMessage: null,
    };
  }

  const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  // Prefer latestMessage timestamps when available.
  const lm = chat.latestMessage;
  if (lm) {
    const lmRaw =
      lm.editedAt ||
      lm.createdAt ||
      lm.time ||
      lm.timestamp ||
      (typeof lm === "object" ? lm?.sentAt : null);

    const source = lm.editedAt
      ? "latestMessage.editedAt"
      : lm.createdAt
        ? "latestMessage.createdAt"
        : lm.time
          ? "latestMessage.time"
          : lm.timestamp
            ? "latestMessage.timestamp"
            : typeof lm === "object" && lm?.sentAt
              ? "latestMessage.sentAt"
              : null;

    const ts = toMillis(lmRaw);
    if (ts) {
      return {
        timestamp: ts,
        source,
        rawValue: lmRaw,
        latestMessage: lm,
      };
    }
  }

  // Fallback to chat-level timestamps (for chats with no messages yet)
  const chatRaw =
    chat.updatedAt || chat.createdAt || chat.time || chat.timestamp;

  const source = chat.updatedAt
    ? "chat.updatedAt"
    : chat.createdAt
      ? "chat.createdAt"
      : chat.time
        ? "chat.time"
        : chat.timestamp
          ? "chat.timestamp"
          : null;

  return {
    timestamp: toMillis(chatRaw),
    source,
    rawValue: chatRaw,
    latestMessage: lm || null,
  };
};

// Backwards-compatible helper: numeric timestamp only
const getChatSortTimestamp = (chat) => getChatSortInfo(chat).timestamp;

const ChatList = ({ onChatSelect }) => {
  const {
    chats,
    selectedChatId,
    currentUser,
    loading,
    error,
    setChats,
    setLoading,
    setError,
    selectChat,
    updateChat,
  } = useSecureChat();

  const [searchQuery, setSearchQuery] = useState("");
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [filteredChats, setFilteredChats] = useState([]);

  // Fetch chats on mount
  const fetchChats = useCallback(async () => {
    try {
      setLoading("chats", true);
      setError(null);

      const response = await api.getChats();
      const chatsData = response?.data || response || [];

      // Populate user data for each chat
      let populatedChats = await Promise.all(
        chatsData.map(async (chat) => {
          try {
            // Get other users in the chat (exclude current user)
            const otherUsers = chat.users.filter(
              (user) => user._id !== currentUser._id
            );

            return {
              ...chat,
              otherUsers,
              displayName: chat.isGroupChat
                ? chat.chatName
                : otherUsers[0]?.name || "Unknown User",
              displayPic: chat.isGroupChat
                ? chat.groupImage
                : otherUsers[0]?.profilePic || "",
              unreadCount:
                typeof chat.unreadCount === "number"
                  ? chat.unreadCount
                  : Number(chat.unreadCount) || 0,
            };
          } catch (error) {
            console.error("Error populating chat:", error);
            return chat;
          }
        })
      );

      if (!Array.isArray(populatedChats)) {
        populatedChats = [];
      }

      // Sort by latest activity (latest message or createdAt) descending
      const sortedChats = populatedChats
        .slice()
        .sort((a, b) => getChatSortTimestamp(b) - getChatSortTimestamp(a));

      setChats(sortedChats);
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to load chats");
    } finally {
      setLoading("chats", false);
    }
  }, [currentUser, setChats, setLoading, setError]);

  // Filter chats based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredChats(chats);
    } else {
      const filtered = chats.filter((chat) =>
        chat.displayName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChats(filtered);
    }
  }, [chats, searchQuery]);

  // Load chats on mount
  useEffect(() => {
    if (currentUser) {
      setLoading("chats", true);
      fetchChats();
    }
  }, [currentUser, fetchChats]);

  const normalizeId = useCallback((value) => {
    if (!value) return null;
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    if (typeof value === "object") {
      if (value._id) return normalizeId(value._id);
      if (value.id) return normalizeId(value.id);
      if (typeof value.toString === "function") {
        const str = value.toString();
        if (str && str !== "[object Object]") {
          return str;
        }
      }
    }
    return null;
  }, []);

  // Realtime: listeners for chat list events
  useEffect(() => {
    if (!currentUser) return;

    const upsertChat = (incoming) => {
      setChats((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const incomingId = normalizeId(incoming._id || incoming.chatId);
        const idx = prevArr.findIndex((c) => normalizeId(c._id) === incomingId);

        let nextChat;
        const existing = idx >= 0 ? prevArr[idx] : null;
        const existingUnread = existing?.unreadCount ?? 0;
        if (incoming._id) {
          // secure_chat_created with full chat
          const otherUsers = Array.isArray(incoming.users)
            ? incoming.users.filter((u) => u._id !== currentUser._id)
            : [];
          nextChat = {
            ...incoming,
            otherUsers,
            displayName: incoming.isGroupChat
              ? incoming.chatName
              : otherUsers[0]?.name || "Unknown User",
            displayPic: incoming.isGroupChat
              ? incoming.groupImage
              : otherUsers[0]?.profilePic || "",
            unreadCount:
              typeof incoming.unreadCount === "number"
                ? incoming.unreadCount
                : Number(incoming.unreadCount) || existingUnread,
          };
        } else {
          // secure_chat_updated with { chatId, latestMessage, updatedAt }
          const fallbackExisting = existing ?? {
            _id: incoming.chatId,
            unreadCount: 0,
          };

          const latestMessage =
            incoming.latestMessage ?? fallbackExisting.latestMessage;
          const updatedAt = incoming.updatedAt ?? fallbackExisting.updatedAt;

          let nextUnreadCount = existingUnread;

          if (typeof incoming.unreadCount === "number") {
            nextUnreadCount = incoming.unreadCount;
          } else if (incoming.latestMessage && currentUser?._id) {
            const existingLatestId = normalizeId(
              fallbackExisting.latestMessage?._id ||
              fallbackExisting.latestMessage
            );
            const incomingLatestId = normalizeId(
              incoming.latestMessage._id || incoming.latestMessage
            );
            const senderId = normalizeId(
              incoming.latestMessage.sender?._id ||
              incoming.latestMessage.sender
            );
            const isOwnMessage = senderId === normalizeId(currentUser._id);
            const isNewMessage =
              incomingLatestId && incomingLatestId !== existingLatestId;
            const isSystemUpdate = Boolean(
              incoming.latestMessage?.isEdited ||
              incoming.latestMessage?.isDeleted
            );

            if (isNewMessage && !isOwnMessage && !isSystemUpdate) {
              nextUnreadCount = existingUnread + 1;
            }
          }

          nextChat = {
            ...fallbackExisting,
            latestMessage,
            updatedAt,
            unreadCount: nextUnreadCount,
          };
        }

        const list =
          idx >= 0
            ? [...prevArr.slice(0, idx), nextChat, ...prevArr.slice(idx + 1)]
            : [nextChat, ...prevArr];

        // Reorder by latest activity (latest message or createdAt) desc
        const sorted = list
          .slice()
          .sort((a, b) => getChatSortTimestamp(b) - getChatSortTimestamp(a));
        return sorted;
      });
    };

    const offCreated = onSecureChatCreated((chat) => {
      upsertChat(chat);
    });
    const offUpdated = onSecureChatUpdated((payload) => {
      upsertChat(payload);
    });

    return () => {
      try {
        offCreated?.();
      } catch (_) { }
      try {
        offUpdated?.();
      } catch (_) { }
    };
  }, [currentUser, normalizeId, setChats]);

  // Handle chat selection
  const handleChatSelect = useCallback(
    (chatId) => {
      selectChat(chatId);
      updateChat(chatId, { unreadCount: 0 });
      onChatSelect?.(chatId);
    },
    [selectChat, updateChat, onChatSelect]
  );

  const addChatToState = useCallback(
    (chat) => {
      setChats((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const sanitizedChat = {
          ...chat,
          unreadCount:
            typeof chat?.unreadCount === "number"
              ? chat.unreadCount
              : Number(chat?.unreadCount) || 0,
        };

        const existingIndex = prevArr.findIndex(
          (item) => item._id === sanitizedChat._id
        );

        if (existingIndex >= 0) {
          const existingChat = prevArr[existingIndex];

          // intelligent merge
          let mergedLatestMessage = sanitizedChat.latestMessage;
          if (
            existingChat.latestMessage &&
            typeof existingChat.latestMessage === "object" &&
            (typeof sanitizedChat.latestMessage === "string" ||
              !sanitizedChat.latestMessage)
          ) {
            mergedLatestMessage = existingChat.latestMessage;
          }

          const next = [...prevArr];
          next[existingIndex] = {
            ...existingChat,
            ...sanitizedChat,
            latestMessage: mergedLatestMessage,
          };
          return next;
        }

        const next = [sanitizedChat, ...prevArr];
        return next;
      });
    },
    [setChats]
  );

  const handleNewChat = useCallback(
    (chat) => {
      addChatToState(chat);
      setShowNewChatModal(false);
      // Select and open the newly created chat
      selectChat(chat._id);
      onChatSelect?.(chat._id);
    },
    [addChatToState, selectChat, onChatSelect]
  );

  const handleNewGroup = useCallback(
    (chat) => {
      addChatToState(chat);
      setShowNewGroupModal(false);
    },
    [addChatToState]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchChats();
  }, [fetchChats]);

  if (loading.chats) {
    return (
      <Container>
        <Header>
          <Title>Chats</Title>
        </Header>
        <LoadingContainer>
          <LoadingText>Loading chats...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>Chats</Title>
        </Header>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={handleRefresh}>Retry</RetryButton>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Chats</Title>
        <HeaderActions>
          <NewGroupButton onClick={() => setShowNewGroupModal(true)}>
            <Users size={18} aria-hidden="true" />
            <span className="sr-only">New group</span>
          </NewGroupButton>
          <NewChatButton onClick={() => setShowNewChatModal(true)}>
            <User size={18} aria-hidden="true" />
            <span className="sr-only">New chat</span>
          </NewChatButton>
        </HeaderActions>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search chats..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      <ChatsContainer>
        {filteredChats.length === 0 ? (
          <EmptyContainer>
            <EmptyText>
              {searchQuery ? "No chats found" : "No chats yet"}
            </EmptyText>
            <EmptySubtext>
              {searchQuery
                ? "Try a different search term"
                : "Start a new conversation"}
            </EmptySubtext>
            {!searchQuery && (
              <ButtonRow>
                <NewGroupButton onClick={() => setShowNewGroupModal(true)}>
                  Create Group
                </NewGroupButton>
                <NewChatButton onClick={() => setShowNewChatModal(true)}>
                  Start Chatting
                </NewChatButton>
              </ButtonRow>
            )}
          </EmptyContainer>
        ) : (
          filteredChats.map((chat) => (
            <ChatListItem
              key={chat._id}
              chat={chat}
              isSelected={selectedChatId === chat._id}
              onSelect={() => handleChatSelect(chat._id)}
            />
          ))
        )}
      </ChatsContainer>

      {showNewChatModal && (
        <NewChatModal
          onClose={() => setShowNewChatModal(false)}
          onChatCreated={handleNewChat}
        />
      )}

      {showNewGroupModal && (
        <NewGroupModal
          onClose={() => setShowNewGroupModal(false)}
          onGroupCreated={handleNewGroup}
        />
      )}
    </Container>
  );
};

export default ChatList;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--color-surface);
  border-left: 1px solid var(--color-border);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  background-color: var(--color-surface);
  min-height: 60px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  font-family: "Merriweather", serif;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NewChatButton = styled.button`
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &:hover {
    background-color: var(--color-background);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SearchContainer = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px; /* Changed from 20px to 8px */
  font-size: 14px;
  outline: none;
  background-color: var(--color-background);
  color: var(--color-text);
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-text);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const ChatsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: var(--color-surface);
  scroll-behavior: smooth;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  /* WebKit */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
`;

const LoadingText = styled.p`
  color: var(--color-text);
  font-size: 16px;
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
`;

const ErrorText = styled.p`
  color: var(--color-text);
  font-size: 16px;
  margin: 0 0 16px 0;
  text-align: center;
`;

const RetryButton = styled.button`
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: var(--color-background);
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyText = styled.p`
  color: var(--color-text);
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;

const EmptySubtext = styled.p`
  color: var(--color-text-secondary);
  font-size: 14px;
  margin: 0 0 24px 0;
`;

const NewGroupButton = styled.button`
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &:hover {
    background-color: var(--color-background);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;
